import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { palette } from 'theme/palette';
import { Box, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import {
  IconBuildingStore,
  IconCirclePlus,
  IconEye,
  IconEyeClosed,
  IconFlag,
} from '@tabler/icons-react';
import { Snackbar } from '@atoms';
import { CUSTOMER_SUPPORT, OPERATIONS, OPERATIONS_SUPERVISOR, PERMISSIONS } from '@globalConstants';
import {
  useCompanyDetails,
  useRoles,
  useSnackbar,
  useURLparams,
  useAreasByCustomer,
  useTabSelector,
  useCreateArea,
  useCompanyMovements,
  usePermissions,
} from '@hooks';
import { FullWidthContent, BoxContainer, MainMenuLayout } from '@layouts';
import { CardHeader, GoBackButton, TabSelector, AreaCardDetail } from '@molecules';
import { AreaCreationModal, ClientMovementsTable, SelectCustomer } from '@organisms';
import { parseAmount } from '@utils';
import usePostalAddresByCustomerId from './data/PlacesRepository/address/hooks/usePostalAddressByCustomerId';
import AddCompanyPostalAddress from './modules/AddCompanyPostalAddress/AddCompanyPostalAddress';
import CompanyDetails from './modules/CompanyDetails/CompanyDetails';
import styles from './styles';

/**
 * Clients (partners) screen for internal users
 */
const ClientsScreen = () => {
  const { t } = useTranslation();
  const { validateRoles } = useRoles();
  const isOperationRole = validateRoles([OPERATIONS, OPERATIONS_SUPERVISOR]);
  const canSeeDetailsPermission = validateRoles([
    OPERATIONS,
    OPERATIONS_SUPERVISOR,
    CUSTOMER_SUPPORT,
  ]);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const navigate = useNavigate();
  const { params, setURLparams } = useURLparams();
  const customerId = params.customerId;
  const { data, isLoading, mutate: mutateCompany } = useCompanyDetails(customerId);
  const {
    data: areas,
    isLoading: isAreasLoading,
    mutate: mutateAreas,
  } = useAreasByCustomer({
    customerId: customerId,
  });
  const [addAreaModalOpen, setAddAreaModalOpen] = useState(false);
  const { isLoading: isCreateAreasLoading, createArea } = useCreateArea();
  const canCreateAreas = usePermissions([PERMISSIONS.createAreas]);

  const {
    data: companyPostalAddress,
    isLoading: isLoadingCompanyPostalAddress,
    mutate,
  } = usePostalAddresByCustomerId(customerId);

  const itemsSelector = useMemo(() => {
    if (!data && !areas?.length) return [];
    const formattedData =
      data && Object.keys(data).length > 0
        ? { ...data, balance: data.balanceAvailable ?? data.balance, isCompany: true }
        : null;
    return formattedData ? [formattedData, ...areas] : areas;
  }, [data, areas]);

  const { selectedItem: selectedArea, setSelectedItem: setSelectedArea } = useTabSelector({
    data: itemsSelector,
    propKey: 'id',
  });

  /**
   * Function to prevent additional custom params being overwritten
   */
  function handleNewSearchParams(newValues) {
    setURLparams({
      ...(params.tab && { tab: params.tab }),
      ...(params.customerId && { customerId: params.customerId }),
      ...newValues,
    });
  }

  const {
    filtersOpen,
    setFiltersOpen,
    isLoading: companyMovementsIsLoading,
    data: companyMovementsData,
    handlePageChange,
    handleRowsPerPageChange,
    onFiltersFormSubmit,
    mutate: companyMovementsMutate,
  } = useCompanyMovements(
    {
      params,
      setURLparams: handleNewSearchParams,
    },
    !selectedArea?.isCompany && selectedArea?.id
  );

  const [showBalance, setShowBalance] = useState(false);

  const responsive = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const clientMenu = [
    {
      text: t('EMPLOYEES:DETAIL:VIEW_DETAIL'),
      canViewOption: params.tab !== 'details',
      onClick: () => {
        setURLparams({ ...params, tab: 'details' });
      },
    },
    {
      text: t('COMMON:SEE_REPORTS'),
      canViewOption: false,
      onClick: () => {
        navigate(`/reportes-operaciones?customerId=${customerId}`, { state: { goBack: -1 } });
      },
    },
    {
      text: t('CLIENTS:DETAIL:ADD_POSTAL_ADDRESS:TITLE'),
      canViewOption: params.tab === 'details' && isOperationRole,
      onClick: () => {
        setURLparams({ ...params, tab: 'add_postal_address' });
      },
    },
  ];

  /** Change balance visibility */
  const onChangeBalance = () => {
    setShowBalance(!showBalance);
  };

  const balance = data?.balance ? parseAmount(data.balance) : '-';
  const leftComponentOptions = {
    type: 'avatar',
    icon: <IconBuildingStore color={palette.primary.main} />,
  };
  const rightComponentOptions = {
    type: 'text',
    text: !showBalance ? balance : '$ *****',
    icon: !showBalance ? <IconEye /> : <IconEyeClosed />,
    onClickIcon: onChangeBalance,
    menuOptions: clientMenu,
  };
  const subtitleOptions = [
    {
      text: data?.cuil,
    },
    ...(data?.customerType
      ? [
          {
            text: t(`CLIENTS:DETAIL:${data.customerType}`),
            variant: 'h4',
          },
        ]
      : []),
  ];

  /** Go to previous screen */
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** Render content depending of "tab" selected */
  const renderContent = (tabSelected) => {
    switch (tabSelected) {
      case 'details': {
        return (
          <>
            <CompanyDetails
              customerId={data.id}
              balanceAvailable={data.balanceAvailable}
              companyPostalAddress={companyPostalAddress}
              isLoading={isLoadingCompanyPostalAddress}
              isOperationRole={canSeeDetailsPermission}
            />
            <GoBackButton variant='outlined' />
          </>
        );
      }
      case 'add_postal_address': {
        return (
          <AddCompanyPostalAddress
            customerId={customerId}
            navigateBack={goBack}
            navigateToDetails={(result) => {
              setURLparams({ ...params, tab: 'details', result }, { replace: true });
            }}
          />
        );
      }
      default: {
        return (
          <>
            {selectedArea && itemsSelector?.length > 0 && (
              <TabSelector
                selectedTab={selectedArea}
                items={getParsedAreas()}
                keyProp='id'
                titleProp='title'
                rightButton={
                  canCreateAreas && {
                    icon: <IconCirclePlus />,
                    text: t('CLIENTS:CREATE_AREA:ADD'),
                    onClick: () => {
                      setAddAreaModalOpen(true);
                    },
                  }
                }
              />
            )}
            <Box sx={styles.containerAreaMovements(responsive)}>
              {selectedArea && (
                <AreaCardDetail
                  area={selectedArea}
                  fetchAreas={mutateAreas}
                  fetchCompany={mutateCompany}
                  fetchMovements={companyMovementsMutate}
                />
              )}
              <ClientMovementsTable
                {...{
                  filtersOpen,
                  setFiltersOpen,
                  isLoading: companyMovementsIsLoading,
                  data: companyMovementsData,
                  handlePageChange,
                  handleRowsPerPageChange,
                  onFiltersFormSubmit,
                  client: data,
                  idArea: !selectedArea?.isCompany && selectedArea?.id,
                }}
              />
            </Box>
            <GoBackButton backFunction={() => setURLparams({})} variant='outlined' />
          </>
        );
      }
    }
  };

  useEffect(() => {
    if (params.result && params.result === 'success') {
      // Clean result params from URL for that user can navigate back to the client screen
      goBack();

      mutate();
      showSnackbar(t('CLIENTS:DETAIL:ADD_POSTAL_ADDRESS:SUCCESS'), 'success');
    }
  }, [goBack, mutate, params.result, setURLparams, showSnackbar, t]);

  /**
   * Submits the creation of an area with the values given in the modal
   */
  const onSubmitAreaCreation = async (values) => {
    await createArea(values);
    mutateAreas();
    setAddAreaModalOpen(false);
  };

  /**
   * add icon, title and onclick function to each area
   */
  const getParsedAreas = () => {
    const items = itemsSelector?.map((item) => {
      return {
        ...item,
        icon: item.isCompany ? <IconBuildingStore /> : <IconFlag />,
        title: (
          <Box style={styles.titleStyle}>
            {item.isCompany ? item.description : item.fantasyName}
            <b>{Boolean(item.balance) || item.balance === 0 ? parseAmount(item.balance) : ''}</b>
          </Box>
        ),
        onClick: () => {
          setURLparams({
            ...(params.tab && { tab: params.tab }),
            ...(params.customerId && { customerId: params.customerId }),
          });
          setSelectedArea(item);
        },
      };
    });
    return items;
  };

  return (
    <>
      <MainMenuLayout>
        <FullWidthContent>
          {customerId ? (
            isLoading || isAreasLoading ? (
              <Box sx={styles.spinner}>
                <CircularProgress />
              </Box>
            ) : (
              data && (
                <>
                  <CardHeader
                    withMenu={
                      canSeeDetailsPermission &&
                      params.tab !== 'add_postal_address' &&
                      (params.tab !== 'details' || !companyPostalAddress)
                    }
                    title={data?.description}
                    leftComponentOptions={leftComponentOptions}
                    rightComponentOptions={rightComponentOptions}
                    subtitleOptions={subtitleOptions}
                  />
                  {renderContent(params.tab)}
                </>
              )
            )
          ) : (
            <>
              <BoxContainer>
                <Typography variant='h4'>{t('COMMON:COMPANY')}</Typography>
                <SelectCustomer />
              </BoxContainer>
            </>
          )}
          <AreaCreationModal
            isOpen={addAreaModalOpen}
            isLoading={isCreateAreasLoading}
            onSubmit={onSubmitAreaCreation}
            closeModal={() => setAddAreaModalOpen(false)}
          />
        </FullWidthContent>
      </MainMenuLayout>
      <Snackbar {...snackbarProps} />
    </>
  );
};

export default ClientsScreen;
